/**
 * useHostMessages Hook
 *
 * Provides message threads for host dashboard from Firestore.
 */

import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, orderBy, getDocs, onSnapshot } from 'firebase/firestore';
import { db } from '../../../../services/firebase';
import { useAuth } from '../../../../contexts/AuthContext';

export interface MessageThread {
  id: string;
  guestId: string;
  guestName: string;
  guestAvatar: string;
  listingId: string;
  listingTitle: string;
  listingImage: string;
  bookingId?: string;
  bookingStatus?: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  lastMessage: string;
  lastMessageAt: Date;
  lastMessageBy: 'host' | 'guest';
  unreadCount: number;
  isArchived: boolean;
  isMuted: boolean;
}

export interface Message {
  id: string;
  threadId: string;
  senderId: string;
  senderType: 'host' | 'guest';
  content: string;
  createdAt: Date;
  isRead: boolean;
}

type FilterType = 'all' | 'unread' | 'active' | 'pending' | 'archived';

export const useHostMessages = (filter: FilterType = 'all') => {
  const { user } = useAuth();
  const [threads, setThreads] = useState<MessageThread[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchThreads = useCallback(async () => {
    if (!user) {
      setThreads([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Threads are in `messageThreads`; the host is one of `participants`.
      const threadsRef = collection(db, 'messageThreads');
      const q = query(threadsRef, where('participants', 'array-contains', user.uid));
      const snap = await getDocs(q);

      const mapped: MessageThread[] = snap.docs.map((doc) => {
        const d = doc.data();
        const otherParticipantId = (d.participants as string[] | undefined)?.find(
          (p) => p !== user.uid
        ) || '';
        const other = (d.otherParticipant as { id?: string; name?: string; photo?: string } | undefined);
        // unreadCounts is denormalized per-participant so each user sees their own badge.
        const unreadForMe = (d.unreadCounts && d.unreadCounts[user.uid]) || d.unreadCount || 0;
        const lastMessageContent =
          typeof d.lastMessage === 'string'
            ? d.lastMessage
            : (d.lastMessage?.content as string | undefined) || '';
        // The thread doc stores lastMessageBy as the senderId. The
        // MessageThread type wants 'guest' | 'host' - derive from whether
        // the sender is the current host user.
        const rawLastMessageBy = d.lastMessageBy as string | undefined;
        const lastMessageBy: 'guest' | 'host' =
          rawLastMessageBy === user.uid || rawLastMessageBy === 'host'
            ? 'host'
            : 'guest';
        return {
          id: doc.id,
          guestId: other?.id || otherParticipantId,
          guestName: other?.name || 'Guest',
          guestAvatar: other?.photo || '',
          listingId: d.listing?.id || '',
          listingTitle: d.listing?.title || '',
          listingImage: d.listing?.photo || '',
          bookingId: d.bookingId,
          bookingStatus: d.bookingStatus,
          lastMessage: lastMessageContent,
          lastMessageAt: d.lastMessageAt?.toDate?.() || new Date(),
          lastMessageBy,
          unreadCount: unreadForMe,
          isArchived: d.isArchived || false,
          isMuted: d.isMuted || false,
        };
      });

      let filtered = mapped;
      switch (filter) {
        case 'unread':
          filtered = mapped.filter((t) => t.unreadCount > 0);
          break;
        case 'active':
          filtered = mapped.filter(
            (t) => t.bookingStatus === 'confirmed' || t.bookingStatus === 'active'
          );
          break;
        case 'pending':
          filtered = mapped.filter((t) => t.bookingStatus === 'pending');
          break;
        case 'archived':
          filtered = mapped.filter((t) => t.isArchived);
          break;
      }

      setThreads(filtered);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch message threads:', err);
      setThreads([]);
      setError(null); // Don't show error for empty state
    } finally {
      setIsLoading(false);
    }
  }, [user, filter]);

  useEffect(() => {
    fetchThreads();
  }, [fetchThreads]);

  const totalUnread = threads.reduce((sum, t) => sum + t.unreadCount, 0);

  return {
    threads,
    totalUnread,
    isLoading,
    error,
    refetch: fetchThreads,
  };
};

export const useThreadMessages = (threadId: string | null) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!threadId) {
      setMessages([]);
      return;
    }

    setIsLoading(true);

    try {
      // Unified data model: messages live in flat `messages` collection
      // tagged with `threadId`. Subscribe so new messages appear live.
      const messagesRef = collection(db, 'messages');
      const q = query(messagesRef, where('threadId', '==', threadId), orderBy('createdAt', 'asc'));

      const unsubscribe = onSnapshot(q, (snap) => {
        const msgs: Message[] = snap.docs.map((doc) => {
          const d = doc.data();
          return {
            id: doc.id,
            threadId,
            senderId: d.senderId || '',
            senderType: d.senderType || (d.senderId ? 'host' : 'guest'),
            content: d.content || '',
            createdAt: d.createdAt?.toDate?.() || new Date(),
            isRead: d.read === true || d.isRead === true,
          };
        });
        setMessages(msgs);
        setIsLoading(false);
      }, () => {
        setMessages([]);
        setIsLoading(false);
      });

      return () => unsubscribe();
    } catch {
      setMessages([]);
      setIsLoading(false);
    }
  }, [threadId]);

  return { messages, isLoading };
};

export default useHostMessages;
